import { loadStripe } from '@stripe/stripe-js';
import { getBaseUrl } from '../config/apiConfig';
import apiService from '../backend/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const getApiUrl = () => `${getBaseUrl()}/api`;

export const initiateOneTimePayment = async (packageName, price, username) => {
  try {
    const API_URL = getApiUrl();

    const stripe = await stripePromise;
    if (!stripe) {
      console.error('Stripe konnte nicht initialisiert werden');
      throw new Error('Zahlungsanbieter konnte nicht initialisiert werden.');
    }

    const data = await apiService.request(`${API_URL}/payment/create-checkout-session`, {
      method: 'POST',
      body: JSON.stringify({
        packageName,
        price,
        paymentType: 'one-time',
        username: username || 'anonymous'
      }),
    });


    if (data.error) {
      throw new Error(data.error);
    }

    if (!data.id) {
      throw new Error('Keine Session-ID vom Server erhalten');
    }


    await delay(100);

    const result = await stripe.redirectToCheckout({
      sessionId: data.id,
    });

    if (result.error) {
      console.error('Stripe Redirect Fehler:', result.error.message);
      throw new Error('Fehler beim Weiterleiten zum Zahlungsanbieter: ' + result.error.message);
    }
  } catch (error) {
    console.error(`Detaillierter Fehler: ${error.name}: ${error.message}`);
    throw error;
  }
};

// Funktion für Abonnements (monatliche Pakete)
export const initiateSubscription = async (planName, priceMonthly, username, proration = null) => {
  try {
    const API_URL = getApiUrl();
    //console.log(`Starte Abonnement für ${planName} zum Preis von ${priceMonthly}€/Monat`);

    if (proration) {
      console.log('💰 Upgrade mit Proration:', proration);
    }

    // Stelle sicher, dass Stripe geladen wurde
    const stripe = await stripePromise;
    if (!stripe) {
      console.error('Stripe konnte nicht initialisiert werden');
      throw new Error('Zahlungsanbieter konnte nicht initialisiert werden.');
    }

    // API-Aufruf an den Backend-Server via apiService
    //console.log('Sende Anfrage an Backend via apiService...');
    const requestBody = {
      planName,
      priceMonthly,
      paymentType: 'subscription',
      username: username || 'anonymous'
    };

    // Füge Proration-Daten hinzu, falls vorhanden (für Upgrades)
    if (proration) {
      requestBody.proration = proration;
    }

    const data = await apiService.request(`${API_URL}/payment/create-subscription`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });

    //console.log('Antwort vom Server erhalten via apiService:', data);

    if (data.error) {
      throw new Error(data.error);
    }

    if (!data.id) {
      throw new Error('Keine Session-ID vom Server erhalten');
    }

    //console.log('Session-ID erhalten, leite zu Stripe weiter:', data.id);

    // Kurze Verzögerung vor Weiterleitung
    await delay(100);

    // Leite den Benutzer zur Stripe-Checkout-Seite weiter
    const result = await stripe.redirectToCheckout({
      sessionId: data.id,
    });

    if (result.error) {
      console.error('Stripe Redirect Fehler:', result.error.message);
      throw new Error('Fehler beim Weiterleiten zum Zahlungsanbieter: ' + result.error.message);
    }
  } catch (error) {
    console.error(`Detaillierter Fehler: ${error.name}: ${error.message}`);
    throw error;
  }
};

// Funktion für intelligenten Abo-Wechsel (sofort)
export const initiateSmartSubscriptionChange = async (newPlanName, username) => {
  try {
    const API_URL = getApiUrl();
    //console.log(`Starte intelligenten Abo-Wechsel zu ${newPlanName} für ${username}`);

    // API-Aufruf an den Backend-Server via apiService
    const data = await apiService.request(`${API_URL}/payment/smart-subscription-change`, {
      method: 'POST',
      body: JSON.stringify({
        username: username || 'anonymous',
        newPlanName
      }),
    });

    //console.log('Erhaltene Daten via apiService:', data);

    if (data.error) {
      throw new Error(data.error);
    }

    return data;

  } catch (error) {
    console.error(`Detaillierter Fehler: ${error.name}: ${error.message}`);
    throw error;
  }
};

// Funktion für geplanten Abo-Wechsel (zum nächsten Abrechnungszeitraum)
export const scheduleSubscriptionChange = async (newPlanName, username, upgradeMode = 'immediate') => {
  try {
    const API_URL = getApiUrl();
    //console.log(`Plane Abo-Wechsel zu ${newPlanName} für ${username} zum nächsten Abrechnungszeitraum`);

    // Hole Plan-Details dynamisch vom Backend
    const plans = await apiService.request(`${API_URL}/payment/plans`);
    const planDetails = plans.find(p => p.name === newPlanName);

    if (!planDetails) {
      throw new Error(`Unbekannter Plan: ${newPlanName}`);
    }

    // API-Aufruf an den Backend-Server via apiService
    console.log('🚀 Sending to backend:', { username, newPlanName, upgradeMode }); // DEBUG
    const data = await apiService.request(`${API_URL}/payment/schedule-subscription-change`, {
      method: 'POST',
      body: JSON.stringify({
        username: username || 'anonymous',
        newPlanName,
        newPriceMonthly: planDetails.price,
        upgradeMode: upgradeMode // 'immediate' oder 'next_period' - explizit
      }),
    });

    //console.log('Erhaltene Daten via apiService:', data);

    if (data.error) {
      throw new Error(data.error);
    }

    // SPECIAL: Upgrade-Differenz über Checkout bezahlen (Fallback wenn direkte Zahlung fehlschlägt)
    if (data.requiresCheckout && data.checkoutUrl) {
      console.log('💳 Upgrade-Differenz über Checkout-Session:', data.checkoutUrl);
      window.location.href = data.checkoutUrl;
      return data;
    }

    // SPECIAL: Wenn Free → Paid Wechsel, dann muss Stripe-Checkout gestartet werden
    if (data.requiresPayment) {
      console.log('Free → Paid Wechsel erkannt, starte Stripe-Checkout...');
      // Starte normalen Subscription-Flow
      await initiateSubscription(newPlanName, planDetails.price, username);
      return data;
    }

    // Upgrade/Downgrade wurde bereits in Stripe aktualisiert, keine weitere Aktion nötig
    console.log('✅ Abo-Wechsel erfolgreich geplant');
    return data;

  } catch (error) {
    console.error(`Detaillierter Fehler: ${error.name}: ${error.message}`);
    throw error;
  }
};

// Stripe Billing Portal öffnen — Zahlungsmethode aktualisieren
export const openBillingPortal = async (username) => {
  try {
    const API_URL = getApiUrl();

    const data = await apiService.request(`${API_URL}/payment/create-billing-portal-session`, {
      method: 'POST',
      body: JSON.stringify({
        username: username || 'anonymous'
      }),
    });

    if (data.error) {
      throw new Error(data.error);
    }

    if (!data.url) {
      throw new Error('Keine Portal-URL vom Server erhalten');
    }

    // Weiterleitung zum Stripe Billing Portal
    window.location.href = data.url;
  } catch (error) {
    console.error(`Fehler beim Öffnen des Zahlungsportals: ${error.message}`);
    throw error;
  }
};

// Funktion zum Stornieren eines geplanten Abo-Wechsels
export const cancelScheduledChange = async (username) => {
  try {
    const API_URL = getApiUrl();
    //console.log(`Storniere geplanten Abo-Wechsel für ${username}`);

    // API-Aufruf an den Backend-Server via apiService
    const data = await apiService.request(`${API_URL}/payment/cancel-scheduled-change`, {
      method: 'POST',
      body: JSON.stringify({
        username: username || 'anonymous'
      }),
    });

    //console.log('Erhaltene Daten via apiService:', data);

    if (data.error) {
      throw new Error(data.error);
    }

    return data;

  } catch (error) {
    console.error(`Detaillierter Fehler: ${error.name}: ${error.message}`);
    throw error;
  }
};
